import { Grid, Loader, Table } from "semantic-ui-react";

import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MdClose, MdModeEditOutline, MdSave } from "react-icons/md";
import { DropDown, InputText, TitleView } from "../../../../components";
import {
  dataTypeOptions,
  storageEnvTypeOptions,
  WEIGHT_UNIT_TYPES_OPTIONs
} from "../../../../config/constants";
import { IngredientDataTypesOption } from "../../../../config/enums/IngredientType.enum";
import { StorageEnvType } from "../../../../config/enums/StorageType.enum";
import { checkValueIsEmpty } from "../../../../utils/utils";

import { usePutFarmGateInternalStorageData } from "../../../../api/cultivations";
import { usePutProcessGateInternalStorageData } from "../../../../api/process";
import { InputNumber } from "../../../../components/InputText/InputNumber";
import InputWithDropDown from "../../../../components/inputWithDropDown/InputWithDropDown";
import { MEASUREMENT_TYPES } from "../../../../config/enums/MeasurementTypes";
import "./ProcessFarmGateStorage.scss";

const ProcessFarmGateStorage = ({ reviewData, refetch, isEditable }: any) => {
  const [selectStorage, setSelectStorage] = useState<any>(null);
  const [dataType, setDataType] = useState<string | undefined>(IngredientDataTypesOption.SPECIFIC);
  const [storageEnvType, setStorageEnvType] = useState<number | undefined>(StorageEnvType.AMBIENT);
  const [weightUnitValue, setWeightUnit] = useState(MEASUREMENT_TYPES.WEIGHT_KG);
  const { mutate: putInternalProcessStorageDataMutate, isLoading: isLoadingProcessStorage } =
    usePutProcessGateInternalStorageData();
  const { mutate: putInternalFarmStorageDataMutate, isLoading: isLoadingFarmStorage } =
    usePutFarmGateInternalStorageData();

  const storageData = useCallback(
    (currnetData: any, packageStatus: any) => {
      return (
        currnetData
          ?.map((storage: any) => {
            const processOrFarmGateName = storage?.[packageStatus] || "";
            const storageDetails = storage?.internalStorage || [];
            const storageData: any[] = [];
            if (storageDetails.length === 0) return null;
            const storageArray = storageDetails || [];
            for (let i = 0; i < storageArray.length; i++) {
              const storageItem = storageArray[i];
              storageData.push(storageItem);
            }
            return {
              gateId: storage?._id,
              processOrFarmGateName,
              gateType: packageStatus === "processGateName" ? "Process Gate" : "Farm Gate",
              storageData
            };
          })
          .filter(Boolean) || []
      );
    },
    [reviewData]
  );

  const processFarGateStorageData = useMemo(() => {
    return [
      ...storageData(reviewData?.processGate || [], "processGateName"),
      ...storageData(reviewData?.farmGate || [], "farmGateName")
    ];
  }, [reviewData, storageData]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm<any>({
    defaultValues: {
      processFarGateStorageData
    }
  });

  const onSubmit = (data: any) => {
    let result = data.processFarGateStorageData.reduce((acc: any, gate: any) => {
      const updatedStorage = gate.storageData.find((t: any) => t._id === selectStorage?._id);
      if (updatedStorage) {
        return {
          processGateId: gate.gateId,
          farmGateId: gate.gateId,
          gateType: gate.gateType,
          internalStorageId: updatedStorage._id,
          storageData: updatedStorage
        };
      }
      return acc;
    }, null);

    if (result?.gateType === "Process Gate") {
      putInternalProcessStorageDataMutate(result, {
        onSuccess: () => {
          setSelectStorage(null);
          refetch();
        }
      });
    } else {
      putInternalFarmStorageDataMutate(result, {
        onSuccess: () => {
          setSelectStorage(null);
          refetch();
        }
      });
    }
  };

  const editData = (row: any) => {
    setSelectStorage(row);
    setDataType(row?.dataType);
    setStorageEnvType(row?.storageEnv);
    setWeightUnit(row?.weightUnit);
    reset({
      processFarGateStorageData: processFarGateStorageData.map((gate: any) => ({
        ...gate,
        storageData: gate.storageData.map((t: any) =>
          t._id === row._id
            ? {
                ...t,
                weight: t.weight,
                distance: t.distance
              }
            : t
        )
      }))
    });
  };

  const handleCancel = () => {
    setSelectStorage(null);
    reset();
  };

  return (
    <>
      <TitleView CustomTitleViewMain="mt-2" title="Warehouse (Process and Farm Gate)" />
      <Table celled className="table" structured>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell className="tableHeader" colSpan="" textAlign="center">
              Warehouse (Process and Farm Gate)
            </Table.HeaderCell>
            <Table.HeaderCell
              className="tableHeader"
              colSpan="15"
              textAlign="center"
              style={{ backgroundColor: "#f4c6db", color: "#000" }}
            >
              Input field
            </Table.HeaderCell>
          </Table.Row>
          <Table.Row>
            <Table.HeaderCell className="tableHeaderReview">Gate</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">External Storage Name</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Data Type</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Storage Environment</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">
              Storage Temperature (℃)
            </Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Warehouse size (m3)</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Weight</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Electricity (kWh)</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">
              Utilization of warehouse %
            </Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview">Storage day(s)</Table.HeaderCell>
            <Table.HeaderCell className="tableHeaderReview"></Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body className="customTransportDropDown">
          {processFarGateStorageData.map((storageDetails: any, dataIndex: number) => {
            const processOrFarmGateName = storageDetails?.processOrFarmGateName;
            const gateType = storageDetails?.gateType;
            const storageDataArray = storageDetails?.storageData || [];

            let renderedRows: JSX.Element[] = [];
            let currentRow = 0;

            for (let groupIndex = 0; groupIndex < storageDataArray.length; groupIndex++) {
              const data = storageDataArray[groupIndex];
              const isSelectStorage = selectStorage?._id === data?._id;
              const storageEnv = storageEnvTypeOptions.find((e: any) => e.key === data?.storageEnv);
              const weightUnit = WEIGHT_UNIT_TYPES_OPTIONs.find(
                (e: any) => e.key === data?.weightUnit
              );
              const rowLength = storageDataArray.length;

              renderedRows.push(
                <Table.Row key={`${dataIndex}-${groupIndex}-empty`}>
                  {currentRow === 0 && (
                    <Table.Cell rowSpan={rowLength}>
                      {processOrFarmGateName} ({gateType})
                    </Table.Cell>
                  )}
                  <Table.Cell className={checkValueIsEmpty(data?.internalStorageName)}>
                    {isSelectStorage ? (
                      <InputText
                        register={register}
                        errors={errors.internalStorageName}
                        placeholder="Internal Storage Name"
                        name={`processFarGateStorageData.${dataIndex}.storageData.${groupIndex}.internalStorageName`}
                        required={true}
                        errorMessage={"Please enter Internal Storage Name"}
                      />
                    ) : (
                      <> {data?.internalStorageName || "Missing storage name"}</>
                    )}
                  </Table.Cell>
                  <Table.Cell className={checkValueIsEmpty(data?.dataType)}>
                    {isSelectStorage ? (
                      <DropDown
                        required={true}
                        placeholder="Select Storage Type"
                        upward={true}
                        currentData={dataTypeOptions}
                        defaultValue={dataType}
                        customGridColumn={"customGridColumnType"}
                        handleChangeState={(e: any, { value }: any) => {
                          setDataType(value);
                          setValue(
                            `processFarGateStorageData.${dataIndex}.storageData.${groupIndex}.dataType`,
                            value,
                            { shouldDirty: true, shouldValidate: true }
                          );
                        }}
                      />
                    ) : (
                      <>{data?.dataType || "Missing data type"}</>
                    )}
                  </Table.Cell>
                  <Table.Cell className={checkValueIsEmpty(storageEnv?.text)}>
                    {isSelectStorage ? (
                      <DropDown
                        required={true}
                        placeholder="Select Storage Environment"
                        upward={true}
                        currentData={storageEnvTypeOptions}
                        defaultValue={storageEnvType}
                        customGridColumn={"customGridColumnType"}
                        handleChangeState={(e: any, { value }: any) => {
                          setStorageEnvType(value);
                          setValue(
                            `processFarGateStorageData.${dataIndex}.storageData.${groupIndex}.storageEnv`,
                            value,
                            { shouldDirty: true, shouldValidate: true }
                          );
                        }}
                      />
                    ) : (
                      <> {storageEnv?.text || "Missing storage enviroment"}</>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {isSelectStorage ? (
                      <InputNumber
                        transform={{
                          input: (value: any) =>
                            isNaN(value) || value === 0 ? "" : value.toString(),
                          output: (e: any) => {
                            return e;
                          }
                        }}
                        control={control}
                        placeholder="Storage Temperature (℃)"
                        name={`processFarGateStorageData.${dataIndex}.storageData.${groupIndex}.storageTemperature`}
                        enableMinus={true}
                        enableDecimalWithMinus
                      />
                    ) : (
                      <>{data?.storageTemperature}</>
                    )}
                  </Table.Cell>

                  <Table.Cell>
                    {isSelectStorage ? (
                      <InputNumber
                        transform={{
                          input: (value: any) =>
                            isNaN(value) || value === 0 ? "" : value.toString(),
                          output: (e: any) => {
                            return e;
                          }
                        }}
                        control={control}
                        placeholder="Warehouse size (m3)"
                        name={`processFarGateStorageData.${dataIndex}.storageData.${groupIndex}.warehouseSize`}
                        errorMessage="Warehouse size is required"
                      />
                    ) : (
                      <> {data?.warehouseSize}</>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {isSelectStorage ? (
                      <InputWithDropDown
                        defaultSelectValue={weightUnitValue}
                        options={WEIGHT_UNIT_TYPES_OPTIONs}
                        selectOption={(value: number) => {
                          setWeightUnit(value);
                          setValue(
                            `processFarGateStorageData.${dataIndex}.storageData.${groupIndex}.weightUnit`,
                            value,
                            { shouldDirty: true, shouldValidate: true }
                          );
                        }}
                        control={control}
                        placeholder="Weight"
                        name={`processFarGateStorageData.${dataIndex}.storageData.${groupIndex}.weight`}
                        defaultValue={0}
                        transform={{
                          input: (value: any) =>
                            isNaN(value) || value === 0 ? "" : value.toString(),
                          output: (e: any) => {
                            return e;
                          }
                        }}
                      />
                    ) : (
                      <>
                        {data?.weight} {weightUnit?.text}
                      </>
                    )}
                  </Table.Cell>
                  <Table.Cell className={checkValueIsEmpty(data?.electricity)}>
                    {isSelectStorage ? (
                      <InputNumber
                        control={control}
                        placeholder="Electricity"
                        name={`processFarGateStorageData.${dataIndex}.storageData.${groupIndex}.electricity`}
                        transform={{
                          input: (v: any) =>
                            v === null || v === undefined || isNaN(v) ? "" : v.toString(),
                          output: (e: any) => e
                        }}
                        errors={
                          (errors?.processFarGateStorageData as any)?.[dataIndex]?.storageData?.[
                            groupIndex
                          ]?.electricity
                        }
                        required
                        errorMessage="Electricity is required"
                      />
                    ) : (
                      <>{data?.electricity || "Missing electricity"}</>
                    )}
                  </Table.Cell>
                  <Table.Cell className={checkValueIsEmpty(data?.utilizationOfWarehouse)}>
                    {isSelectStorage ? (
                      <InputNumber
                        control={control}
                        placeholder="Utilization of Warehouse"
                        name={`processFarGateStorageData.${dataIndex}.storageData.${groupIndex}.utilizationOfWarehouse`}
                        transform={{
                          input: (v: any) =>
                            v === null || v === undefined || isNaN(v) ? "" : v.toString(),
                          output: (e: any) => e
                        }}
                        errors={
                          (errors?.processFarGateStorageData as any)?.[dataIndex]?.storageData?.[
                            groupIndex
                          ]?.utilizationOfWarehouse
                        }
                        required
                        errorMessage="Utilization of Warehouse is required"
                      />
                    ) : (
                      <> {data?.utilizationOfWarehouse || "Missing utilization of warehouse"}</>
                    )}
                  </Table.Cell>
                  <Table.Cell className={checkValueIsEmpty(data?.storageDays)}>
                    {isSelectStorage ? (
                      <InputNumber
                        control={control}
                        placeholder="Storage Days"
                        name={`processFarGateStorageData.${dataIndex}.storageData.${groupIndex}.storageDays`}
                        transform={{
                          input: (v: any) =>
                            v === null || v === undefined || isNaN(v) ? "" : v.toString(),
                          output: (e: any) => e
                        }}
                        errors={
                          (errors?.processFarGateStorageData as any)?.[dataIndex]?.storageData?.[
                            groupIndex
                          ]?.storageDays
                        }
                        required
                        errorMessage="Storage Days is required"
                      />
                    ) : (
                      <>{data?.storageDays || "Missing storage days"}</>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <Grid>
                      <Grid.Column computer={4} tablet={5} mobile={5}>
                        {(isLoadingProcessStorage || isLoadingFarmStorage) && isSelectStorage ? (
                          <Loader active size="small" inline="centered" />
                        ) : isSelectStorage ? (
                          <MdSave
                            cursor="pointer"
                            size={24}
                            color="var(--tableEditIcon)"
                            title="Save"
                            onClick={handleSubmit(onSubmit)}
                          />
                        ) : (
                          <>
                            {isEditable && (
                              <MdModeEditOutline
                                cursor="pointer"
                                size={24}
                                color="var(--tableEditIcon)"
                                onClick={() => editData(data)}
                              />
                            )}
                          </>
                        )}
                      </Grid.Column>
                      {!(isLoadingProcessStorage || isLoadingFarmStorage) && isSelectStorage ? (
                        <Grid.Column computer={4} tablet={16} mobile={16}>
                          <MdClose
                            cursor={"pointer"}
                            size={24}
                            color="var(--tableEditIcon)"
                            onClick={() => handleCancel()}
                          />
                        </Grid.Column>
                      ) : null}
                    </Grid>
                  </Table.Cell>
                </Table.Row>
              );
              currentRow++;
            }
            return renderedRows;
          })}
        </Table.Body>
      </Table>
    </>
  );
};

export default ProcessFarmGateStorage;
